import React from "react";
import styled from "@emotion/styled";
import { css, useTheme } from "@emotion/react";

/**
 * The artwork that sits inside FeaturePromoFrame.
 *
 * Deliberately isolated so a future feature launch can drop in a different
 * graphic without touching the frame or the panel around it. Nothing here is
 * configurable — the whole component is what gets swapped.
 *
 * Everything is composed at a fixed width and never scales; the frame only ever
 * reveals more or less gradient around it. The queue runs off the bottom edge on
 * purpose, so its bottom corners are squared.
 *
 * The marks are drawn locally rather than pulled from the Plato feature's
 * FitIndicator: this is a static light-surface illustration, and FitIndicator
 * recolours itself from t.dark, which would repaint these against a card that
 * stays white in both themes.
 */

const fitGradientId = "plato-promo-fit-gradient";

// The sparkle used across the Plato surfaces, on a 24x24 viewBox.
const sparklePath =
  "M9.937 15.5A2 2 0 0 0 8.5 14.063l-6.135-1.582a.5.5 0 0 1 0-.962L8.5 9.936A2 2 0 0 0 9.937 8.5l1.582-6.135a.5.5 0 0 1 .962 0L14.063 8.5A2 2 0 0 0 15.5 9.937l6.135 1.582a.5.5 0 0 1 0 .962L15.5 14.063a2 2 0 0 0-1.437 1.437l-1.582 6.135a.5.5 0 0 1-.962 0z";

// fit buckets to empty / half / full, matching the Plato tab's dial.
const queueRows = [
  { name: "Sarah Mitchell", fit: 5 },
  { name: "Daniela Vasilev", fit: 5 },
  { name: "Alexia Laurent", fit: 5 },
  { name: "Guillaume Ledogard", fit: 3 },
  { name: "Ricardo David Oliveira", fit: 1 },
  { name: "Ravi Patron", fit: 1 },
  { name: "Marcus Lindqvist", fit: 3 },
  { name: "Priya Raman", fit: 3 },
  { name: "Daniel Brooks", fit: 5 },
  { name: "Lena Hofmann", fit: 3 },
  { name: "David Chen", fit: 5 },
  { name: "Emily Ross", fit: 3 },
  { name: "James Ward", fit: 1 },
  { name: "Rachel Kim", fit: 3 },
  { name: "Kevin Walsh", fit: 1 },
  { name: "Lauren Bennett", fit: 5 },
];

const scoredRows = [
  { name: "Sarah Mitchell", tag: "Excellent fit", value: 5 },
  { name: "Daniel Brooks", tag: "Excellent fit", value: 5 },
  { name: "Alexia Laurent", tag: "Excellent fit", value: 5 },
];

function PromoDial({ fit, size }: { fit: number; size: number }) {
  const t: any = useTheme();
  const radius = size / 2 - 1;
  const center = size / 2;
  const fraction = fit <= 1 ? 0 : fit <= 3 ? 0.5 : 1;

  let wedge = null;
  if (fraction >= 1) {
    wedge = <circle cx={center} cy={center} r={radius} fill={t.color.gray[300]} />;
  } else if (fraction > 0) {
    const theta = fraction * 2 * Math.PI;
    const endX = center + radius * Math.sin(theta);
    const endY = center - radius * Math.cos(theta);
    wedge = (
      <path
        d={`M${center},${center} L${center},${
          center - radius
        } A${radius},${radius} 0 0,1 ${endX.toFixed(2)},${endY.toFixed(2)} Z`}
        fill={t.color.gray[300]}
      />
    );
  }

  return (
    <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
      <circle
        cx={center}
        cy={center}
        r={radius}
        fill="none"
        stroke={t.color.gray[400]}
        strokeWidth={1.25}
      />
      {wedge}
    </svg>
  );
}

function PromoStars({ value, size, gap }: { value: number; size: number; gap: number }) {
  const t: any = useTheme();
  const scale = size / 24;
  const totalWidth = 5 * size + 4 * gap;

  return (
    <svg width={totalWidth} height={size} viewBox={`0 0 ${totalWidth} ${size}`}>
      {[1, 2, 3, 4, 5].map((position) => {
        const isFilled = position <= value;
        return (
          <path
            key={position}
            d={sparklePath}
            transform={`translate(${(position - 1) * (size + gap)},0) scale(${scale})`}
            fill={isFilled ? `url(#${fitGradientId})` : "none"}
            stroke={isFilled ? "none" : t.color.gray[300]}
            strokeWidth={isFilled ? 0 : 1.5}
          />
        );
      })}
    </svg>
  );
}

export default function PlatoPromoGraphic() {
  return (
    <Styled.Group aria-hidden>
      <Styled.GradientDefs>
        <svg width="0" height="0" focusable="false">
          <defs>
            <linearGradient id={fitGradientId} x1="0" y1="0" x2="1" y2="0">
              <stop offset="0%" stopColor="#f1bc8c" />
              <stop offset="100%" stopColor="#e3a1da" />
            </linearGradient>
          </defs>
        </svg>
      </Styled.GradientDefs>

      <Styled.Queue>
        {queueRows.map((row) => (
          <Styled.QueueRow key={row.name}>
            <Styled.QueueCheck />
            <Styled.QueueName>{row.name}</Styled.QueueName>
            <PromoDial fit={row.fit} size={11} />
          </Styled.QueueRow>
        ))}
      </Styled.Queue>

      <Styled.Card>
        <Styled.CardHeader>Inbox</Styled.CardHeader>
        <Styled.CardBody>
          {scoredRows.map((row) => (
            <Styled.ScoredRow key={row.name}>
              <Styled.ScoredName>{row.name}</Styled.ScoredName>
              <Styled.ScoredTag>{row.tag}</Styled.ScoredTag>
              <PromoStars value={row.value} size={15} gap={2} />
            </Styled.ScoredRow>
          ))}
        </Styled.CardBody>
      </Styled.Card>
    </Styled.Group>
  );
}

/* Styled Components
======================================================= */
let Styled: any;
Styled = {};

Styled.Group = styled.div`
  label: PlatoPromoGraphic_Group;
  position: absolute;
  left: 50%;
  top: 40px;
  bottom: 0;
  /* The queue and the card are positioned against each other inside this fixed
     width — it is what keeps their relationship from drifting. */
  width: 406px;
  transform: translateX(-50%);
`;

Styled.GradientDefs = styled.div`
  label: PlatoPromoGraphic_GradientDefs;
  position: absolute;
  width: 0;
  height: 0;
  overflow: hidden;
`;

Styled.Queue = styled.div((props: any) => {
  const t: any = props.theme;

  return css`
    label: PlatoPromoGraphic_Queue;
    position: absolute;
    left: 0;
    top: 0;
    width: 240px;
    overflow: hidden;
    background-color: ${t.color.white};
    border: 1px solid ${t.color.gray[200]};
    /* Squared at the bottom — the list runs off the frame and is never seen. */
    border-radius: 0.625rem 0.625rem 0 0;
    box-shadow: 0 12px 40px rgba(0, 0, 0, 0.12);
  `;
});

Styled.QueueRow = styled.div((props: any) => {
  const t: any = props.theme;

  return css`
    label: PlatoPromoGraphic_QueueRow;
    display: flex;
    align-items: center;
    gap: 9px;
    height: 31px;
    ${t.px(2)}

    svg {
      display: block;
      flex-shrink: 0;
    }
  `;
});

Styled.QueueCheck = styled.span((props: any) => {
  const t: any = props.theme;

  return css`
    label: PlatoPromoGraphic_QueueCheck;
    width: 13px;
    height: 13px;
    flex-shrink: 0;
    box-sizing: border-box;
    ${t.rounded.xs}
    border: 1.5px solid #dadada;
  `;
});

Styled.QueueName = styled.span((props: any) => {
  const t: any = props.theme;

  return css`
    label: PlatoPromoGraphic_QueueName;
    flex: 1;
    min-width: 0;
    ${[t.text.xs, t.text.medium]}
    color: ${t.color.gray[500]};
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  `;
});

Styled.Card = styled.div((props: any) => {
  const t: any = props.theme;

  return css`
    label: PlatoPromoGraphic_Card;
    position: absolute;
    left: 50px;
    top: 48px;
    width: 356px;
    background-color: ${t.color.white};
    border: 1px solid ${t.color.gray[200]};
    border-radius: 0.75rem;
    box-shadow: 0 22px 70px rgba(0, 0, 0, 0.22), 0 2px 8px rgba(0, 0, 0, 0.08);
  `;
});

Styled.CardHeader = styled.div((props: any) => {
  const t: any = props.theme;

  return css`
    label: PlatoPromoGraphic_CardHeader;
    display: flex;
    align-items: center;
    padding: 12px 16px 10px;
    border-bottom: 1px solid #f0f0f0;
    ${t.text.bold}
    font-size: 0.9375rem;
    letter-spacing: -0.01em;
    color: ${t.color.gray[900]};
  `;
});

Styled.CardBody = styled.div`
  label: PlatoPromoGraphic_CardBody;
  padding: 4px 16px 0;
`;

Styled.ScoredRow = styled.div`
  label: PlatoPromoGraphic_ScoredRow;
  display: flex;
  align-items: center;
  gap: 10px;
  height: 42px;
`;

Styled.ScoredName = styled.span((props: any) => {
  const t: any = props.theme;

  return css`
    label: PlatoPromoGraphic_ScoredName;
    flex: 1;
    min-width: 0;
    ${[t.text.sm, t.text.medium]}
    color: ${t.color.gray[900]};
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  `;
});

Styled.ScoredTag = styled.span((props: any) => {
  const t: any = props.theme;

  return css`
    label: PlatoPromoGraphic_ScoredTag;
    display: inline-flex;
    align-items: center;
    flex-shrink: 0;
    height: 22px;
    ${[t.px(2), t.rounded.xs, t.text.semibold]}
    font-size: 0.71875rem;
    white-space: nowrap;
    color: ${t.color.gray[900]};
    background: linear-gradient(120deg, #fbd7ff 10%, #ffdec1 90%);
    box-shadow: inset 0 0 0 1px rgba(0, 0, 0, 0.06);
  `;
});
